import type { LivePhoto as LivePhotoDataType } from '@/@types/common'
import { css } from '@emotion/react'
import clsx from 'clsx'
import {
	type HTMLAttributes,
	type SyntheticEvent,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import LivePhotoIcon from '../Icons/LivePhotoIcon'

export interface Player {
	play: () => void
	pause: () => void
	toggle: () => void
	stop: () => void
}

export interface PlayerEventHandlerProps {
	onPhotoLoad?: (player: Player, event: SyntheticEvent<HTMLImageElement, Event>) => void
	onVideoLoad?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onCanPlay?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onEnded?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onError?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
}

export interface LivePhotoProps
	extends Omit<HTMLAttributes<HTMLDivElement>, keyof PlayerEventHandlerProps>,
		PlayerEventHandlerProps {
	data: LivePhotoDataType | undefined
	width?: number
	height?: number

	isPreview?: boolean
}

export interface LivePhotoHandle {
	player: Player | null
	root: HTMLDivElement | null
}

export default forwardRef<LivePhotoHandle, LivePhotoProps>(function LivePhoto(
	{
		width,
		height,
		data,
		onPhotoLoad,
		onVideoLoad,
		onCanPlay,
		onEnded,
		onError,
		isPreview = false,
		...props
	},
	ref
) {
	const [isPlaying, setIsPlaying] = useState<boolean>(false)
	const imgRef = useRef<HTMLImageElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const rootRef = useRef<HTMLDivElement>(null)
	const play = useCallback(() => {
		videoRef.current?.play()
	}, [])

	const pause = useCallback(() => {
		videoRef.current?.pause()
	}, [])

	const toggle = useCallback(() => {
		if (videoRef.current?.paused) {
			play()
		} else {
			pause()
		}
	}, [pause, play])

	const reset = useCallback(() => {
		videoRef.current && (videoRef.current.currentTime = 0)
	}, [])

	const stop = useCallback(() => {
		pause()
		reset()
	}, [pause, reset])

	const playerRef = useRef<Player>({
		play,
		pause,
		toggle,
		stop,
	})

	useImperativeHandle(ref, () => ({
		get player() {
			return playerRef.current
		},
		get root() {
			return rootRef.current
		},
	}))

	const mediaStyle = useMemo(
		() => css`
			width: 100%;
			height: 100%;
			object-fit: ${isPreview ? 'cover' : 'contain'};
			display: block;
		`,
		[isPreview]
	)
	const containerStyle = useMemo(
		() => css`
			position: relative;
			height: 100%;
			${width && height && `aspect-ratio: ${width} / ${height};`}
		`,
		[width, height]
	)
	const videoStyle = useMemo(
		() => css`
			position: absolute;
			inset: 0;
			z-index: 1;
			opacity: 0;
			transition: opacity 0.3s;

			&.live-photo-playing {
				opacity: 1;
			}
			${isPreview
				? css`
						width: 100%;
						height: 100%;
						object-fit: cover;
						display: block;
					`
				: css`
						width: 100%;
						height: 100%;
						object-fit: contain;
						display: block;
					`};
		`,
		[isPreview]
	)

	if (!data) {
		return null
	}

	return (
		<div ref={rootRef} {...props} css={containerStyle}>
			<img
				ref={imgRef}
				src={data.imageUrl}
				alt="LivePhoto"
				className="live-photo-cover"
				referrerPolicy="no-referrer"
				loading="lazy"
				css={mediaStyle}
				onLoad={(event: SyntheticEvent<HTMLImageElement, Event>) =>
					onPhotoLoad?.(playerRef.current, event)
				}
			/>
			<video
				ref={videoRef}
				src={isPreview ? undefined : data.videoUrl}
				className={clsx('live-photo-video', {
					'live-photo-playing': isPlaying,
				})}
				css={videoStyle}
				playsInline
				muted
				controls={false}
				onLoadedData={(event: SyntheticEvent<HTMLVideoElement, Event>) =>
					onVideoLoad?.(playerRef.current, event)
				}
				onCanPlay={(event: SyntheticEvent<HTMLVideoElement, Event>) =>
					onCanPlay?.(playerRef.current, event)
				}
				onEnded={(event: SyntheticEvent<HTMLVideoElement, Event>) => {
					reset()
					return onEnded?.(playerRef.current, event)
				}}
				onError={(event: SyntheticEvent<HTMLVideoElement, Event>) =>
					onError?.(playerRef.current, event)
				}
				onPlay={() => {
					setIsPlaying(true)
				}}
				onPause={() => {
					setIsPlaying(false)
				}}
			/>
			{/* live photo badge */}
			<div
				onMouseOver={() => {
					play()
				}}
				onMouseLeave={() => {
					stop()
				}}
				css={css`
					position: absolute;
					top: 6px;
					left: 6px;
					z-index: 1;
					padding: 2px 4px 2px 2px;
					border-radius: 20px;
					background: var(--solid-gray-1);
					display: flex;
					align-items: center;
					gap: 2px;
				`}
			>
				<LivePhotoIcon
					css={css`
						width: 12px;
						height: 12px;
						color: var(--solid-gray-4);
					`}
				/>

				<span
					css={css`
						color: var(--solid-gray-4);
						font-size: 10px;
						font-weight: 500;
						line-height: 12px;
					`}
				>
					LIVE
				</span>
			</div>
		</div>
	)
})
